import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../../api';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';

const EditRole = () => {
  const { id } = useParams();
  const [role, setRole] = useState('');
  const [permissions, setPermissions] = useState([]); // All permissions grouped
  const [stats, setStats] = useState([]); // Stats permissions
  const [selected, setSelected] = useState([]); // Selected permissions
  const [selectedStats, setSelectedStats] = useState([]); // Selected stats permissions
  const [activeTab, setActiveTab] = useState('details');
  const [permTab, setPermTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [roleRes, permsRes] = await Promise.all([
          apiGet(`/api/admin/roles/list`, {}),
          apiGet('/api/admin/roles/permissions', {}),
        ]);
        const found = (roleRes.data.data.data || []).find(r => r._id === id);
        if (!found) throw new Error('Role not found');
        setRole(found.role);
        setSelected(found.permissions || []);
        setSelectedStats(found.dashboard_permissions || []);
        setPermissions(permsRes.data.data || []);
        setStats(permsRes.data.stats || []);
      } catch (err) {
        toast.error('Failed to fetch role or permissions');
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, [id]);

  const handlePermissionToggle = (perm) => {
    setSelected((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const handleStatsToggle = (stat) => {
    setSelectedStats((prev) =>
      prev.includes(stat) ? prev.filter((s) => s !== stat) : [...prev, stat]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!role) return toast.error('Role name is required');
    setLoading(true);
    try {
      await apiPost(`/api/admin/roles/edit/${id}`, {
        role,
        permissions: selected,
        dashboard_permissions: selectedStats,
      });
      toast.success('Role updated successfully');
      navigate('/roles');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  // Combine permissions and stats for tab display
  const allGroups = [
    ...permissions,
    ...(stats.length > 0 ? [{ group: 'stats', permissions: Object.fromEntries(stats.map(stat => [stat, stat])) }] : [])
  ];

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Management
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground mb-6">Edit <span className="text-gradient-ginox">Role</span></h1>

      {/* Section tabs: Details / Permissions */}
      <div className="flex gap-2 mb-6">
        <button
          type="button"
          onClick={() => setActiveTab('details')}
          className={
            activeTab === 'details'
              ? 'rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-1.5 text-xs font-medium text-ginox-green transition-colors'
              : 'rounded-md border border-border/70 bg-white/[0.02] px-4 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors'
          }
        >
          Details
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('permissions')}
          className={
            activeTab === 'permissions'
              ? 'rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-1.5 text-xs font-medium text-ginox-green transition-colors'
              : 'rounded-md border border-border/70 bg-white/[0.02] px-4 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors'
          }
        >
          Permissions
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {activeTab === 'details' && (
          <div className="p-5 mb-6">
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Role Name
            </label>
            <input
              type="text"
              value={role}
              onChange={e => setRole(e.target.value)}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
              placeholder="Enter role name"
              required
            />
          </div>
        )}

        {activeTab === 'permissions' && (
          <div className="p-5 mb-6">
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">
              Permissions
            </label>

            {/* Permission group tabs */}
            <div className="flex flex-wrap gap-2 mb-4">
              {allGroups.map((group, idx) => (
                <button
                  type="button"
                  key={group.group}
                  onClick={() => setPermTab(idx)}
                  className={
                    permTab === idx
                      ? 'rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1 text-xs font-medium text-ginox-green transition-colors'
                      : 'rounded-md border border-border/70 bg-white/[0.02] px-3 py-1 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors'
                  }
                >
                  {group.group}
                </button>
              ))}
            </div>

            {/* Permission checkboxes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {allGroups[permTab] && allGroups[permTab].group === 'stats' ? (
                stats.map((stat) => (
                  <label key={stat} className="flex items-center gap-2 cursor-pointer group/check">
                    <input
                      type="checkbox"
                      checked={selectedStats.includes(stat)}
                      onChange={() => handleStatsToggle(stat)}
                      className="h-3.5 w-3.5 rounded border border-border/70 bg-white/[0.03] accent-ginox-green"
                    />
                    <span className="text-sm text-muted-foreground group-hover/check:text-foreground transition-colors">{stat}</span>
                  </label>
                ))
              ) : (
                allGroups[permTab] && Object.entries(allGroups[permTab].permissions).map(([label, perm]) => (
                  <label key={perm} className="flex items-center gap-2 cursor-pointer group/check">
                    <input
                      type="checkbox"
                      checked={selected.includes(perm)}
                      onChange={() => handlePermissionToggle(perm)}
                      className="h-3.5 w-3.5 rounded border border-border/70 bg-white/[0.03] accent-ginox-green"
                    />
                    <span className="text-sm text-muted-foreground group-hover/check:text-foreground transition-colors">{label}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
            onClick={() => navigate('/roles')}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditRole;
